/**
 * Sortable Gallery Grid
 * 
 * Drag-and-drop gallery grid component using @dnd-kit.
 * Handles reordering and calls onReorder callback.
 */

import {
  DndContext,
  closestCenter,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragStartEvent, DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { useState, useEffect } from 'react';
import { SortableImageItem, type GalleryImage } from './SortableImageItem';

interface SortableGalleryGridProps {
  images: GalleryImage[];
  onReorder: (newOrder: GalleryImage[]) => void;
  onSetPrimary?: (imageName: string, isVideo?: boolean) => void;  // Update signature
  isSaving?: boolean;
  disabled?: boolean;
  isChoosingPrimary?: boolean;
  isChoosingPrimaryVideo?: boolean;  // Add this
  isDeleteMode?: boolean;
  isRearranging?: boolean;
  selectedImages?: Set<string>;
  onToggleSelection?: (imageName: string) => void;
  onVideoClick?: (videoUrl: string) => void;  // New prop for video lightbox
  onReplaceItem?: (galleryItemName: string) => void;  // OPS-1554: replace media item
}

const isTouchDevice =
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(pointer: coarse)").matches;

export function SortableGalleryGrid({
  images,
  onReorder,
  onSetPrimary,
  isSaving = false,
  disabled = false,
  isChoosingPrimary = false,
  isChoosingPrimaryVideo = false,  // Add this
  isDeleteMode = false,
  isRearranging = false,
  selectedImages = new Set(),
  onToggleSelection,
  onVideoClick,  // Add this
  onReplaceItem,
}: SortableGalleryGridProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [touchStarted, setTouchStarted] = useState(false);
  // Local state for immediate visual updates, independent of React Query timing
  const [orderedImages, setOrderedImages] = useState<GalleryImage[]>(images);

  // Configure sensors with distance-based activation for quick, reliable touch drag
  // No time delay - only requires small movement (8px) to activate drag
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Tune between 6-12px as needed for optimal feel
      },
    })
  );

  // Sync local state with props when not dragging
  useEffect(() => {
    if (!activeId) {
      setOrderedImages(images);
    }
  }, [images, activeId]);

  useEffect(() => {
    // On touch devices, lock page scroll immediately on touch start, before drag activates
    // This prevents page scroll from interfering with drag initiation
    if (!isTouchDevice) return;
    if (typeof document === "undefined") return;

    const body = document.body;
    const originalOverflow = body.style.overflow;
    const originalTouchAction = body.style.touchAction;
    const originalOverscrollBehavior = (body.style as any).overscrollBehavior;

    // Lock scroll when touch starts OR when drag is active
    if (touchStarted || activeId) {
      // Lock global scroll and gestures immediately on touch
      body.style.overflow = "hidden";
      body.style.touchAction = "none";
      (body.style as any).overscrollBehavior = "contain";
    } else {
      // When not touching/dragging, restore scroll
      body.style.overflow = originalOverflow || "";
      body.style.touchAction = originalTouchAction || "";
      (body.style as any).overscrollBehavior = originalOverscrollBehavior || "";
    }

    return () => {
      // Cleanup: restore original values when component unmounts
      body.style.overflow = originalOverflow || "";
      body.style.touchAction = originalTouchAction || "";
      (body.style as any).overscrollBehavior = originalOverscrollBehavior || "";
    };
  }, [touchStarted, activeId]);

  // Generate consistent IDs for images
  const getImageId = (image: GalleryImage, index: number): string => {
    return image.name || `image-${index}`;
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
    // Once drag is active, we can clear touchStarted (scroll lock now handled by activeId)
    setTouchStarted(false);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    // Always clear touch state on drag end
    setTouchStarted(false);

    if (!over || active.id === over.id) {
      setActiveId(null);
      return;
    }

    const oldIndex = orderedImages.findIndex((img, idx) => getImageId(img, idx) === active.id);
    const newIndex = orderedImages.findIndex((img, idx) => getImageId(img, idx) === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      setActiveId(null);
      return;
    }

    const newOrder = arrayMove(orderedImages, oldIndex, newIndex);

    // 1) Update local state immediately (drives the DnD animation)
    setOrderedImages(newOrder);

    // 2) Then notify parent to persist (React Query mutation)
    onReorder(newOrder);

    setActiveId(null);
  };

  const handleTouchStart = () => {
    if (isTouchDevice && !disabled && !isSaving) {
      setTouchStarted(true);
      // Safety timeout: if drag doesn't activate within 500ms, unlock scroll
      // This handles cases where user touches but doesn't move enough to activate drag
      setTimeout(() => {
        if (!activeId) {
          setTouchStarted(false);
        }
      }, 500);
    }
  };

  const handleTouchEnd = () => {
    // Only clear if drag didn't activate (if activeId is set, handleDragEnd will handle cleanup)
    if (!activeId) {
      setTouchStarted(false);
    }
  };

  const activeImage = activeId
    ? orderedImages.find((img, idx) => getImageId(img, idx) === activeId)
    : null;

  const reorderActive = !!activeId;

  if (!orderedImages || orderedImages.length === 0) {
    return null;
  }

  // In delete mode, disable drag and drop - use same grid layout as normal mode
  if (isDeleteMode) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {orderedImages.map((image, index) => {
          const id = getImageId(image, index);
          return (
            <SortableImageItem
              key={id}
              image={image}
              index={index}
              disabled={false}
              onSetPrimary={onSetPrimary}  // Pass through directly
              showPrimaryControl={false}
              showPrimaryVideoControl={false}
              isActive={false}
              reorderActive={false}
              isDeleteMode={true}
              isRearranging={false}
              isSelected={selectedImages.has(image.name)}
              onToggleSelection={onToggleSelection}
              onVideoClick={onVideoClick}
              onReplace={onReplaceItem ? () => onReplaceItem(image.name) : undefined}
            />
          );
        })}
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={orderedImages.map((img, idx) => getImageId(img, idx))}
        strategy={rectSortingStrategy}
      >
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {orderedImages.map((image, index) => {
            const id = getImageId(image, index);
            return (
              <SortableImageItem
                key={id}
                image={image}
                index={index}
                disabled={disabled || isSaving || !isRearranging}
                onSetPrimary={onSetPrimary}  // Pass through directly - let SortableImageItem decide isVideo param
                showPrimaryControl={isChoosingPrimary}
                showPrimaryVideoControl={isChoosingPrimaryVideo}
                isActive={activeId === id}
                reorderActive={reorderActive}
                isRearranging={isRearranging}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
                onVideoClick={onVideoClick}
                onReplace={onReplaceItem ? () => onReplaceItem(image.name) : undefined}
              />
            );
          })}
        </div>
      </SortableContext>
      <DragOverlay
        style={{ cursor: 'grabbing' }}
        // Disable default drop animation so the overlay disappears
        // exactly where you release the mouse/touch
        dropAnimation={null}
      >
        {activeImage ? (
          <div className="w-[120px] sm:w-[140px] aspect-square rounded overflow-hidden border-2 border-primary/60 ring-4 ring-primary/20 shadow-2xl bg-white dark:bg-gray-900 scale-105 sm:scale-110 transition-transform duration-150 ease-out">
            <img
              src={activeImage.image}
              alt={activeImage.alt_text || 'Image'}
              className="w-full h-full object-cover"
              draggable={false}
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
